/**
 * The aircraft's own photo on its edit form (MB's decision 598, Q523 B; docs/specs/cloud/aircraft-photo.md §4): the
 * photo or what the reports show without one, and for tenant admins, managers and office staff (decision 664)
 * "Upload photo…", "Choose from photos" (the organisation's project photos, those naming the registration first) and
 * "Remove photo". Each saves at once; the database decides (photoApi.ts).
 */
import { useEffect, useRef, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  PHOTO_ACCEPTED_TEXT,
  aircraftPhotoCandidates,
  isCloudAircraftPhoto,
  photoCategoryLabel,
  type Aircraft,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { signedPhotoUrls } from '../portal/photosApi.ts';
import type { PhotoRenderer } from '../portal/photoImages.ts';
import {
  aircraftPhotoUrl,
  loadPhotoChoices,
  removeAircraftPhoto,
  setAircraftPhotoFromFile,
  setAircraftPhotoFromProjectPhoto,
  type PhotoChoice,
} from './photoApi.ts';

export interface AircraftPhotoFieldProps {
  readonly sb: SupabaseClient;
  readonly aircraft: Aircraft;
  /** Tenant admins and managers (display only; the database decides). */
  readonly mayChange: boolean;
  readonly renderer: PhotoRenderer;
  readonly onChanged: (saved: Aircraft) => void;
  /** The new photo's file id (tests); a random UUID by default. */
  readonly newId?: (() => string) | undefined;
}

/** Where the photo shows (help `cloud.aircraft`). */
export const AIRCRAFT_PHOTO_HINT =
  "Shown on the daily report's aircraft page and the weekly report's cover, on each laptop after its next sync.";

const errText = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const choiceTitle = (p: PhotoChoice): string => p.caption.trim() || p.file_name;

export function AircraftPhotoField({
  sb,
  aircraft,
  mayChange,
  renderer,
  onChanged,
  newId,
}: AircraftPhotoFieldProps): ReactElement {
  const reg = aircraft.registration;
  const object = isCloudAircraftPhoto(aircraft.cloud_photo_path) ? aircraft.cloud_photo_path! : null;
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [choices, setChoices] = useState<PhotoChoice[] | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [thumbs, setThumbs] = useState<ReadonlyMap<string, string>>(new Map());
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    setUrl(null);
    if (object) void aircraftPhotoUrl(sb, object).then((u) => live && setUrl(u));
    return () => {
      live = false;
    };
  }, [sb, object]);

  async function run(action: () => Promise<Aircraft>, words: string): Promise<void> {
    setBusy(true);
    setProblem(null);
    setDone(null);
    try {
      const saved = await action();
      onChanged(saved);
      setChoosing(false);
      setDone(words);
    } catch (e) {
      setProblem(errText(e));
    } finally {
      setBusy(false);
    }
  }

  async function openChooser(): Promise<void> {
    setChoosing(true);
    setProblem(null);
    setDone(null);
    try {
      const list = aircraftPhotoCandidates(await loadPhotoChoices(sb, aircraft.tenant_id), reg);
      setChoices(list);
      setThumbs(
        await signedPhotoUrls(
          sb,
          list.map((p) => p.thumb),
        ).catch(() => new Map<string, string>()),
      );
    } catch (e) {
      setProblem(errText(e));
      setChoices([]);
    }
  }

  return (
    <fieldset className="fieldset stack aircraft-photo" aria-label={`Photo of ${reg}`}>
      <legend className="label">Photo</legend>
      {object ? (
        url ? (
          <img className="aircraft-photo__img" src={url} alt={`Photo of ${reg}`} />
        ) : (
          <p className="caption muted">Its own photo is set (loading).</p>
        )
      ) : (
        <p className="body">No photo of its own: the reports show the aircraft-type photo.</p>
      )}
      <p className="caption muted">{AIRCRAFT_PHOTO_HINT}</p>
      {mayChange ? (
        <div className="row row--wrap">
          <Button busy={busy} onClick={() => input.current?.click()}>
            Upload photo…
          </Button>
          <Button busy={busy && choosing} disabled={busy} onClick={() => void openChooser()}>
            Choose from photos
          </Button>
          {object && (
            <Button
              variant="quiet"
              disabled={busy}
              onClick={() =>
                void run(() => removeAircraftPhoto(sb, aircraft), `Removed the photo of ${reg}.`)
              }
            >
              Remove photo
            </Button>
          )}
          <input
            ref={input}
            type="file"
            hidden
            aria-label={`Photo file for ${reg}`}
            accept="image/jpeg,image/png,image/webp,image/bmp,.bmp"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file)
                void run(
                  () => setAircraftPhotoFromFile(sb, aircraft, file, renderer, newId),
                  `${reg}'s photo is now ${file.name}.`,
                );
            }}
          />
        </div>
      ) : (
        <p className="caption muted">
          Tenant admins, managers and office staff set the aircraft&apos;s photo.
        </p>
      )}
      {mayChange && <p className="caption muted">{PHOTO_ACCEPTED_TEXT}.</p>}
      {done && (
        <p className="body" role="status">
          {done}
        </p>
      )}
      {problem && (
        <p className="notice notice--fail" role="alert">
          {problem}
        </p>
      )}
      {choosing && (
        <section className="stack" aria-label={`Choose a photo for ${reg}`}>
          {choices === null ? (
            <p className="caption muted">Loading the photos…</p>
          ) : choices.length === 0 ? (
            <p className="body">
              No project photos in Teslyn Cloud yet. Add photos on a project&apos;s Photos tab, or upload one
              here.
            </p>
          ) : (
            <ul className="photo-grid" aria-label="Project photos">
              {choices.map((p) => (
                <li key={p.id} className="photo-tile">
                  <span className="photo-tile__open">
                    {thumbs.get(p.thumb) ? (
                      <img className="photo-tile__img" src={thumbs.get(p.thumb)} alt="" loading="lazy" />
                    ) : (
                      <span className="photo-tile__none caption muted">Thumbnail not available</span>
                    )}
                  </span>
                  <div className="photo-tile__body">
                    <p className="body photo-tile__title">{choiceTitle(p)}</p>
                    <p className="caption">
                      <span className="chip">{photoCategoryLabel(p.category)}</span>{' '}
                      <span className="data">{p.project_number}</span>
                    </p>
                    <Button
                      disabled={busy}
                      aria-label={`Use ${choiceTitle(p)} as the photo of ${reg}`}
                      onClick={() =>
                        void run(
                          () => setAircraftPhotoFromProjectPhoto(sb, aircraft, p, newId),
                          `${reg}'s photo is now ${choiceTitle(p)} (${p.project_number}).`,
                        )
                      }
                    >
                      Use this photo
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="row">
            <Button variant="quiet" onClick={() => setChoosing(false)}>
              Close the photos
            </Button>
          </div>
        </section>
      )}
    </fieldset>
  );
}
